/**
 * Adapptio Core Library
 *
 * @package lib-core
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEventHandlerMetaData, IEventHandlerResult, Type } from "@adapptio/lib-blueprint";
import {
	IOverlayCloseResult,
	IResolveLinkOpts,
	IToastMessage,
	OVERLAY_CONFIRM_BUTTON_ID,
	OVERLAY_TYPE,
	TLinkLocationSpec,
	TOAST_MESSAGE_TYPE,
	TOverlay
} from "@adapptio/lib-components";
import { ACTION_DELEGATE_STATE, ACTION_ERROR_REASON, IActionDelegate, IActionParams } from "../actions";
import { TCoreComponentEventNodeOpts, TCoreComponentEventNodeTypes } from "./EventNodeTypes";
import { uiTerms } from "../terms/ui";
import { emitEvent } from "@adapptio/lib-shared";

/**
 * Event handler log functions
 * Logs event step details
 */
export type IEventHandlerLogFn = (
	eventType: string,
	opts: unknown,
	metaData: IEventHandlerMetaData,
	result: IEventHandlerResult
) => void;

/**
 * Event method error object
 */
export interface IEventMethodError {
	name: string;
	message: string;
}

/**
 * Event handlers interface
 */
export type TCoreComponentEventHandlers = {
	[K in keyof TCoreComponentEventNodeTypes]: (
		opts: TCoreComponentEventNodeOpts<K>,
		metaData: IEventHandlerMetaData
	) => Promise<IEventHandlerResult>;
};

export interface ICoreComponentEventHandlersOpts {
	/** Event logging functions */
	logEvent: IEventHandlerLogFn;

	/** Function to return action delegate */
	getActionDelegate: (actionId: string, params: IActionParams) => IActionDelegate;

	/** Function to reload all action delegates of specified actionId and params mask */
	reloadActionDelegates: (actionId: string, paramsMask: IActionParams, wait: boolean) => Promise<void>;

	/** Function to navigate client to a link */
	navigate: (linkSpec: TLinkLocationSpec, opts: IResolveLinkOpts, openInNew: boolean) => void;

	/** Function to navigate client to previous link */
	navigateBack: () => void;

	/** Function to enable/disable navigation out of current page */
	setPreventNavigation: (prevent: boolean) => void;

	/** Function to show an overlay */
	openOverlay: (overlaySpec: TOverlay) => Promise<IOverlayCloseResult>;

	/** Function to close an overlay */
	closeOverlay: (overlayId: string, buttonId?: string, customData?: unknown) => void;

	/** Function to show a toast message */
	showToastMessage: (toastMessage: IToastMessage) => string;

	/** Function to hide a toast message */
	hideToastMessage: (toastMessageId: string) => void;

	/** Function to reload session */
	reloadSession: (reloadUserAccount?: boolean) => Promise<void>;
}

const methodErrorType = Type.Object({
	label: uiTerms.events.types.methodError.label,
	props: {
		name: Type.String({
			label: uiTerms.events.types.methodError.nameLabel
		}),
		message: Type.String({
			label: uiTerms.events.types.methodError.messageLabel
		})
	}
});

/**
 * Creates core component event handlers
 *
 * @param config Configuration options
 */
export const createCoreComponentEventHandlers = (
	config: ICoreComponentEventHandlersOpts
): TCoreComponentEventHandlers => ({
	/**
	 * Action
	 * Invokes an action
	 */
	action: async (opts, metaData) => {
		let res: IEventHandlerResult;

		const actionDelegate = config.getActionDelegate(opts.actionId, opts.params);
		await actionDelegate.invoke(true);

		const state = actionDelegate.getState();

		if (state === ACTION_DELEGATE_STATE.LOADED) {
			res = {
				outputName: "onSuccess",
				data: actionDelegate.getData(),
				type: actionDelegate.getTypeDescriptor() || Type.Any({})
			};
		} else {
			let outputName;
			const error = actionDelegate.getLastError();

			if (
				error?.reason === ACTION_ERROR_REASON.USER_ERROR ||
				error?.reason === ACTION_ERROR_REASON.UNHANDLED_ERROR
			) {
				outputName = "onError";
			} else {
				outputName = "onRequestFail";
			}

			res = {
				outputName: outputName,
				data: error,
				type: actionDelegate.getTypeDescriptor() || Type.Any({})
			};
		}

		if (
			(res.outputName === "onError" && !metaData.outputsConnected["onError"]) ||
			(res.outputName === "onRequestFail" && !metaData.outputsConnected["onRequestFail"])
		) {
			config.showToastMessage({
				type: TOAST_MESSAGE_TYPE.ERROR,
				message: uiTerms.events.messages.actionHasFailed,
				details: (res.data as IEventMethodError).message
			});
		}

		config.logEvent("action", opts, metaData, res);
		return res;
	},

	/**
	 * Authorization
	 * Activates output based on a condition
	 * If onError output is connected, displayed an error toast
	 */
	auth: async (opts, metaData) => {
		const res = {
			outputName: opts.condition ? "onAuthorize" : "onError",
			data: opts.condition,
			type: Type.Boolean({
				label: uiTerms.events.types.authAllowed
			})
		};

		if (!opts.condition && !metaData.outputsConnected["onError"]) {
			config.showToastMessage({
				type: TOAST_MESSAGE_TYPE.WARNING,
				message: uiTerms.events.messages.actionNotAllowed
			});
		}

		config.logEvent("auth", opts, metaData, res);
		return res;
	},

	/**
	 * Component Method
	 * Calls a component method
	 */
	cmpMethod: async (opts, metaData) => {
		let res: IEventHandlerResult;
		
		try {
			if (opts.method && typeof opts.method === "function") {
				let retValue = opts.method.apply(opts.method, opts.args);

				// If promise, await
				if (typeof retValue === 'object' && typeof retValue.then === 'function') {
					retValue = await retValue;
				}

				res = {
					outputName: "onSuccess",
					data: retValue,
					type: Type.Any({})
				};
			} else {
				const errObject: IEventMethodError = {
					name: "VALUE_NOT_METHOD",
					message: uiTerms.events.messages.valueIsNotMethod
				};

				res = {
					outputName: "onError",
					data: errObject,
					type: methodErrorType
				};
			}
		} catch (err) {
			const errObject: IEventMethodError = {
				name: err instanceof Error ? err.name : "ERROR",
				message: String(err)
			};

			res = {
				outputName: "onError",
				data: errObject,
				type: methodErrorType
			};
		}

		if (res.outputName === "onError" && !metaData.outputsConnected["onError"]) {
			config.showToastMessage({
				type: TOAST_MESSAGE_TYPE.WARNING,
				message: uiTerms.events.messages.methodHasFailed,
				details: (res.data as IEventMethodError).message
			});
		}

		config.logEvent("cmpMethod", opts, metaData, res);
		return res;
	},

	multiCall: async (opts, metaData) => {
		let res: IEventHandlerResult;
		const errors: IEventMethodError[] = [];
		const returnValues: unknown[] = [];
		const calls: Array<() => Promise<void>> = [];

		for (let i = 0; i < opts.methods.length; i++) {
			const { method, args } = opts.methods[i];

			if (!method || typeof method !== "function") {
				returnValues[i] = null;

				errors[i] = {
					name: "VALUE_NOT_METHOD",
					message: uiTerms.events.messages.valueIsNotMethod
				};
			}

			const exec = async () => {
				try {
					const retValue = method.apply(method, args);
	
					if (typeof retValue === 'object' && typeof retValue.then === 'function') {
						try {
							returnValues[i] = await retValue;
						} catch (err) {
							const errObject: IEventMethodError = {
								name: err instanceof Error ? err.name : "ERROR",
								message: String(err)
							};

							returnValues[i] = null;
							errors[i] = errObject;
						}
					} else {
						returnValues[i] = retValue;
					}
				} catch (err) {
					const errObject: IEventMethodError = {
						name: err instanceof Error ? err.name : "ERROR",
						message: String(err)
					};
	
					returnValues[i] = null;
					errors[i] = errObject;
				}
			};

			calls.push(exec);
		}

		// Wait for promises
		if (opts.runInParallel) {
			await Promise.all(
				calls.map((call) => call())
			);
		} else {
			for (let i = 0; i < calls.length; i++) {
				await calls[i]();

				if (errors.length > 0 && opts.failureMode === "failOnFirst") {
					break;
				}
			}
		}

		// Handle failures
		if (errors.length > 0 && opts.failureMode !== "ignoreErrors") {
			res = {
				outputName: "onError",
				data: {
					name: "METHOD_MULTI_CALL_FAILED",
					message: "One or more method calls have failed",
					results: returnValues,
					errors: errors,
				},
				type: Type.Object({
					props: {
						errors: Type.Array({
							items: [ Type.Any({}) ]
						})
					}
				})
			};
		} else if (opts.failureMode === "ignoreErrors") {
			res = {
				outputName: "onSuccess",
				data: {
					results: returnValues,
					errors: errors
				},
				type: Type.Object({
					props: {
						results: Type.Array({
							items: [ Type.Any({}) ]
						}),
						errors: Type.Array({
							items: [ Type.Any({}) ]
						})
					}
				})
			};
		} else {
			res = {
				outputName: "onSuccess",
				data: returnValues,
				type: Type.Array({
					items: [ Type.Any({}) ]
				})
			};
		}

		if (res.outputName === "onError" && !metaData.outputsConnected["onError"]) {
			config.showToastMessage({
				type: TOAST_MESSAGE_TYPE.WARNING,
				message: uiTerms.events.messages.methodHasFailed,
				details: (res.data as IEventMethodError).message
			});
		}

		config.logEvent("cmpMethod", opts, metaData, res);
		return res;
	},

	/**
	 * Condition
	 * Actives output based on a condition
	 */
	condition: async (opts, metaData) => {
		const res = {
			outputName: opts.condition ? "onTrue" : "onFalse",
			data: opts.condition,
			type: Type.Boolean({
				label: uiTerms.events.types.conditionResult
			})
		};

		config.logEvent("condition", opts, metaData, res);
		return res;
	},

	/**
	 * Variable
	 * Actives success output and returns given value
	 */
	variable: async (opts, metaData) => {
		const res = {
			outputName: "onSuccess",
			data: opts.value,
			type: Type.Any({
				label: uiTerms.events.types.variableValue
			})
		};

		config.logEvent("variable", opts, metaData, res);
		return res;
	},

	/**
	 * Set Return
	 * Set value to be returned from the event handler
	 */
	setReturn: async (opts, metaData) => {
		const res = {
			outputName: "__return__",
			data: opts.value,
			type: Type.Any({
				label: uiTerms.events.types.setReturnValue
			})
		};

		config.logEvent("setReturn", opts, metaData, res);
		return res;
	},

	/**
	 * Set Return
	 * Set value to be returned from the event handler
	 */
	setError: async (opts, metaData) => {
		const res = {
			outputName: "__throw__",
			data: opts,
			type: Type.Any({
				label: uiTerms.events.types.setErrorValue
			})
		};

		config.logEvent("setReturn", opts, metaData, res);
		return res;
	},

	/**
	 * Delay
	 * Activates output after specified timeout
	 */
	delay: async (opts, metaData) => {
		const res = {
			outputName: "onTimeout",
			data: null,
			type: Type.Null({})
		};

		config.logEvent("delay", opts, metaData, res);

		return new Promise((resolve) => {
			setTimeout(() => resolve(res), opts.timeout);
		});
	},

	/**
	 * Emit custom event
	 * Emits a custom event via runtime context
	 */
	emitCustomEvent: async (opts, metaData) => {
		const res = {
			outputName: "onSuccess",
			data: null,
			type: Type.Null({})
		};

		config.logEvent("emitCustomEvent", opts, metaData, res);

		emitEvent(metaData.rCtx.customEvent, opts.eventData);

		return res;
	},

	/**
	 * Action
	 * Invokes an action
	 */
	reloadActionData: async (opts, metaData) => {
		await config.reloadActionDelegates(opts.action.actionId, opts.action.params, opts.waitForData);

		const res: IEventHandlerResult = {
			outputName: "onSuccess",
			data: null,
			type: Type.Void({})
		};

		config.logEvent("action", opts, metaData, res);
		return res;
	},

	/**
	 * Event start
	 * Activates single output without data
	 */
	eventStart: async (opts, metaData) => {
		const res = {
			outputName: "onEvent",
			data: null,
			type: Type.Null({})
		};

		config.logEvent("eventStart", opts, metaData, res);
		return res;
	},

	/**
	 * Navigate
	 * Navigates to an URL address
	 */
	navigate: async (opts, metaData) => {
		const res = {
			outputName: null,
			data: null,
			type: Type.Null({})
		};

		config.navigate(opts.link, {}, opts.openInNew);

		config.logEvent("navigate", opts, metaData, res);
		return res;
	},

	/**
	 * Navigate Back
	 * Navigates to previous URL address
	 */
	navigateBack: async (opts, metaData) => {
		const res = {
			outputName: null,
			data: null,
			type: Type.Null({})
		};

		config.navigateBack();

		config.logEvent("navigateBack", opts, metaData, res);
		return res;
	},

	/**
	 * Set Prevent Navigation
	 * Prevents navigation out of current page
	 */
	setPreventNavigation: async (opts, metaData) => {
		const res = {
			outputName: "onSuccess",
			data: null,
			type: Type.Null({})
		}

		config.setPreventNavigation(opts.prevent);

		config.logEvent("setPreventNavigation", opts, metaData, res);
		return res;
	},

	/**
	 * Reload Session
	 */
	reloadSession: async (opts, metaData) => {
		const res = {
			outputName: "onReload",
			data: null,
			type: Type.Null({})
		};

		config.logEvent("reloadSession", opts, metaData, res);

		await config.reloadSession(opts.reloadUserAccount);

		return res;
	},

	/**
	 * Show Dialog
	 */
	openDialog: async (opts, metaData) => {
		const result = await config.openOverlay({
			type: OVERLAY_TYPE.DIALOG_INFO,
			id: opts.id,
			header: {
				text: opts.headerText,
				icon: opts.headerIcon
			},
			text: opts.text,
			size: opts.size,
			closable: opts.closable,
			buttons: opts.buttons
		});

		const res = {
			outputName: "onClose",
			data: {
				buttonId: result.buttonId,
				customData: result.customData
			},
			type: Type.Object({
				label: uiTerms.events.types.dialogResult,
				props: {
					buttonId: Type.String({
						label: uiTerms.events.types.dialogButtonId
					}),
					customData: Type.Any({
						label: uiTerms.events.types.dialogCustomData
					})
				}
			})
		};

		config.logEvent("openDialog", opts, metaData, res);
		return res;
	},

	/**
	 * Shows confirmation dialog
	 */
	openConfirmationDialog: async (opts, metaData) => {
		const result = await config.openOverlay({
			type: OVERLAY_TYPE.DIALOG_CONFIRMATION,
			id: opts.id,
			header: {
				text: opts.headerText,
				icon: opts.headerIcon
			},
			text: opts.text,
			size: opts.size,
			confirmValue: opts.confirmValue,
			confirmButton: opts.confirmButton,
			cancelButton: opts.cancelButton
		});

		const res = {
			outputName: result.buttonId === OVERLAY_CONFIRM_BUTTON_ID ? "onConfirm" : "onCancel",
			data: null,
			type: Type.Null({})
		};

		config.logEvent("openConfirmationDialog", opts, metaData, res);
		return res;
	},

	/**
	 * Opens dialog with a custom view
	 */
	openViewDialog: async (opts, metaData) => {
		const result = await config.openOverlay({
			type: OVERLAY_TYPE.DIALOG_VIEW,
			id: opts.id,
			header: {
				text: opts.headerText,
				icon: opts.headerIcon
			},
			view: opts.view,
			size: opts.size,
			closable: opts.closable
		});

		const res = {
			outputName: "onClose",
			data: {
				buttonId: result.buttonId,
				customData: result.customData
			},
			type: Type.Object({
				label: uiTerms.events.types.dialogResult,
				props: {
					buttonId: Type.String({
						label: uiTerms.events.types.dialogButtonId
					}),
					customData: Type.Any({
						label: uiTerms.events.types.dialogCustomData
					})
				}
			})
		};

		config.logEvent("openViewDialog", opts, metaData, res);
		return res;
	},

	/**
	 * Opens sidebar with a custom view
	 */
	openViewSidebar: async (opts, metaData) => {
		const result = await config.openOverlay({
			type: OVERLAY_TYPE.SIDEBAR_VIEW,
			id: opts.id,
			header: {
				text: opts.headerText,
				icon: opts.headerIcon
			},
			view: opts.view,
			size: opts.size,
			closable: opts.closable
		});

		const res = {
			outputName: "onClose",
			data: {
				buttonId: result.buttonId,
				customData: result.customData
			},
			type: Type.Object({
				label: uiTerms.events.types.dialogResult,
				props: {
					buttonId: Type.String({
						label: uiTerms.events.types.dialogButtonId
					}),
					customData: Type.Any({
						label: uiTerms.events.types.dialogCustomData
					})
				}
			})
		};

		config.logEvent("openViewSidebar", opts, metaData, res);
		return res;
	},

	/**
	 * Closes dialog or sidebar
	 */
	closeOverlay: async (opts, metaData) => {
		const res = {
			outputName: null,
			data: null,
			type: null
		};

		config.closeOverlay(opts.id, opts.buttonId, opts.customData);

		config.logEvent("closeOverlay", opts, metaData, res);
		return res;
	},

	/**
	 * Show Message
	 * Shows a toast message
	 */
	showMessage: async (opts, metaData) => {
		const res = {
			outputName: null,
			data: null,
			type: Type.Null({})
		};

		let toastType: TOAST_MESSAGE_TYPE;

		switch (opts.type) {
			case "INFO": {
				toastType = TOAST_MESSAGE_TYPE.INFO;
				break;
			}
			case "SUCCESS": {
				toastType = TOAST_MESSAGE_TYPE.SUCCESS;
				break;
			}
			case "WARNING": {
				toastType = TOAST_MESSAGE_TYPE.WARNING;
				break;
			}
			case "ERROR": {
				toastType = TOAST_MESSAGE_TYPE.ERROR;
				break;
			}
		}

		config.showToastMessage({
			id: opts.id,
			type: toastType,
			message: opts.message,
			details: opts.details,
			duration: opts.duration
		});

		config.logEvent("showMessage", opts, metaData, res);
		return res;
	},

	/**
	 * Download file
	 * Downloads a file to the user's computer
	 */	
	downloadFile: async (opts, metaData) => {
		const res = {
			outputName: null,
			data: null,
			type: Type.Null({})
		};

		config.logEvent("downloadFile", opts, metaData, res);

		switch (opts.dataFormat) {
			case "string": {
				const blob = new Blob([ opts.data ], { type: opts.mimeType });
				const url = URL.createObjectURL(blob);

				const link = document.createElement('a');
				link.href = url;

				if (opts.filename) {
					link.download = opts.filename;
				}

				document.body.appendChild(link);
				link.click();

				document.body.removeChild(link);
				URL.revokeObjectURL(url);
				break;
			}
			case "base64": {
				const link = document.createElement('a');
				link.href = 'data:' + opts.mimeType + ';base64,' + opts.data;

				if (opts.filename) {
					link.download = opts.filename;
				}

				document.body.appendChild(link);
				link.click();

				document.body.removeChild(link);
				break;
			}
		}

		return res;
	},

	/**
	 * Read file
	 * Reads a file from the user's computer
	 */	
	readFile: async (opts, metaData) => {
		const res = await new Promise<IEventHandlerResult>((resolve) => {
			const fileInput = document.createElement('input');
			fileInput.type = 'file';
			fileInput.accept = opts.accept;
			
			fileInput.onchange = (event: Event) => {
				const target = event.target as HTMLInputElement;
				const file = target.files?.[0];

				if (!file) {
					resolve({
						outputName: "onCancel",
						data: null,
						type: Type.Null({})
					})
				}

				const reader = new FileReader();

				reader.onload = (event: ProgressEvent<FileReader>) => {
					const result = event.target?.result;

					if (typeof result === 'string') {
						let contents: string;

						switch(opts.dataFormat) {
							case 'base64': {
								// Remove data URL prefix
								contents = result.split(',')[1];
								break;
							}
							default: {
								contents = result;
								break;
							}
						}

						resolve({
							outputName: "onSuccess",
							data: {
								contents: contents,
								filename: file.name,
								mimeType: file.type,
								size: file.size
							},
							type: Type.Object({
								props: {
									contents: Type.String({}),
									filename: Type.String({}),
									mimeType: Type.String({}),
									size: Type.Integer({})
								}
							})
						});
					} else {
						resolve({
							outputName: "onError",
							data: {
								name: "READ_FILE_INVALID_RESULT",
								message: "Invalid FileReader result type.",
								details: result
							},
							type: Type.Object({
								props: {
									name: Type.String({}),
									message: Type.String({})
								}
							})
						});
					}
				};
				
				reader.onerror = (event: ProgressEvent<FileReader>) => {
					console.error("Error reading file using FileReader.", event);

					resolve({
						outputName: "onError",
						data: {
							name: "READ_FILE_ERROR",
							message: "Error reading file using FileReader.",
							details: event
						},
						type: Type.Object({
							props: {
								name: Type.String({}),
								message: Type.String({})
							}
						})
					});
				}

				switch(opts.dataFormat) {
					case 'string': {
						reader.readAsText(file);
						break;
					}
					case 'base64': {
						reader.readAsDataURL(file);
						break;
					}
					default: {
						resolve({
							outputName: "onError",
							data: {
								name: "READ_FILE_INVALID_FORMAT",
								message: "Invalid data format.",
								details: opts.dataFormat
							},
							type: Type.Object({
								props: {
									name: Type.String({}),
									message: Type.String({})
								}
							})
						});
					}
				}
			};

			fileInput.oncancel = () => {
				resolve({
					outputName: "onCancel",
					data: null,
					type: Type.Null({})
				});
			};

			fileInput.click();
			fileInput.remove();
		});

		if (res.outputName === "onError" && !metaData.outputsConnected["onError"]) {
			config.showToastMessage({
				type: TOAST_MESSAGE_TYPE.WARNING,
				message: 'Failed to read file',
				details: (res.data as IEventMethodError).message
			});
		}

		config.logEvent("readFile", opts, metaData, res);

		return res;
	},

	/**
	 * Local Storage / Get
	 * Gets a value from local storage
	 */	
	localStorageGet: async (opts, metaData) => {
		let res: IEventHandlerResult;
		const value = localStorage.getItem(opts.key);

		try {
			const data = value !== null ? JSON.parse(value) : null;

			res = {
				outputName: "onSuccess",
				data: data,
				type: Type.Any({})
			};
		} catch (err) {
			res = {
				outputName: "onError",
				data: {
					name: "LOCAL_STORAGE_JSON_PARSE_ERROR",
					message: "Failed to parse local storage value as JSON: " + String(err)
				},
				type: Type.Object({
					props: {
						name: Type.String({}),
						message: Type.String({})
					}
				})
			};
		}

		config.logEvent("localStorageGet", opts, metaData, res);

		return res;
	},

	/**
	 * Local Storage / Set
	 * Sets a value to local storage
	 */
	localStorageSet: async (opts, metaData) => {
		let res: IEventHandlerResult;

		try {
			const value = JSON.stringify(opts.data ?? null);
			localStorage.setItem(opts.key, value);

			res = {
				outputName: "onSuccess",
				data: opts.data,
				type: Type.Any({})
			};
		} catch (err) {
			res = {
				outputName: "onError",
				data: {
					name: "LOCAL_STORAGE_JSON_STRINGIFY_ERROR",
					message: "Failed to stringify value as JSON: " + String(err)
				},
				type: Type.Object({
					props: {
						name: Type.String({}),
						message: Type.String({})
					}
				})
			};
		}

		config.logEvent("localStorageSet", opts, metaData, res);

		return res;
	},

	/**
	 * Execute JS
	 * Executes a JavaScript code
	 */	
	executeJs: async (opts, metaData) => {
		let res: IEventHandlerResult;

		const wrappedCode = `return (async (vars) => { with (vars) { ${opts.code} } })(vars);`;

		try {
			const exec = new Function("vars", wrappedCode);
			const data = await exec(opts.variables ?? {});

			res = {
				outputName: "onSuccess",
				data: data,
				type: Type.Any({})
			};
		} catch (err) {
			console.error("Error executing JavaScript code:", err);

			res = {
				outputName: "onError",
				data: {
					name: "EXECUTE_JS_ERROR",
					message: String(err),
					details: {
						wrappedCode: wrappedCode,
						variables: opts.variables
					}
				},
				type: Type.Object({
					props: {
						name: Type.String({}),
						message: Type.String({}),
						details: Type.Object({
							props: {
								wrappedCode: Type.String({}),
								variables: Type.Any({})
							}
						})
					}
				})
			};
		}

		config.logEvent("executeJs", opts, metaData, res);

		return res;
	},

	log: async (opts, metaData) => {
		const res = {
			outputName: "onSuccess",
			data: null,
			type: Type.Null({})
		};

		switch (opts.level) {
			case "debug": {
				console.debug(opts.message, opts.data ?? '');
				break;
			}
			case "warn": {
				console.warn(opts.message, opts.data ?? '');
				break;
			}
			case "error": {
				console.error(opts.message, opts.data ?? '');
				break;
			}
			default: {
				console.info(opts.message, opts.data ?? '');
				break;
			}
		}

		config.logEvent("log", opts, metaData, res);

		return res;
	}
});
